import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Plus, Pencil, Trash2, FileText, Search, Smartphone, Copy } from "lucide-react";
import { CustomFieldsInputs } from "../components/CustomFields";

const buildEmpty = (defaultDriverPct, defaultBoPct, defaultPayPerMile) => ({
  name: "", display_id: "", email: "", phone: "", pay_type: "per_mile",
  // Pre-fill $/mile with the Pay Run page's "Single · Per Mile" default so new
  // drivers inherit the fleet rate instead of starting at blank.
  pay_rate: defaultPayPerMile ?? "",
  role: "single", team_partner_id: "", active: true, weekly_deductions: [],
  base_rate_per_mile: "",
  default_back_office_percent: defaultBoPct ?? "",
  cards: [],
});

export default function Drivers() {
  const nav = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [defaultDriverPct, setDefaultDriverPct] = useState("");
  const [defaultBoPct, setDefaultBoPct] = useState("");
  const [defaultPayPerMile, setDefaultPayPerMile] = useState("");
  const [form, setForm] = useState(buildEmpty("", "", ""));
  const [editing, setEditing] = useState(null);
  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmtDriver, setStmtDriver] = useState(null);
  const [stmtPayDate, setStmtPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [stmtStart, setStmtStart] = useState("");
  const [stmtEnd, setStmtEnd] = useState("");
  const [stmtTripIds, setStmtTripIds] = useState([]);
  const [pendingTrips, setPendingTrips] = useState([]);

  const [portalInfo, setPortalInfo] = useState(null); // { driver, pin, url } when modal open

  const enablePortal = async (drv) => {
    try {
      const r = await api.post(`/drivers/${drv.driver_id}/portal-access`);
      const url = `${window.location.origin}/driver${r.data.query}`;
      setPortalInfo({ driver: drv, pin: r.data.pin, url, email_sent: !!r.data.email_sent, email_to: r.data.email_to || "", email_error: r.data.email_error || "" });
      if (r.data.email_sent) {
        toast.success(`PIN emailed to ${r.data.email_to}`);
      } else if (!drv.email) {
        toast.message("No email on file for driver — copy the PIN below and send manually.");
      } else {
        toast.error("Email send failed — copy the PIN below and send manually.");
      }
    } catch (e) {
      toast.error(errMsg(e, "Could not generate portal PIN"));
    }
  };

  const disablePortal = async (drv) => {
    if (!window.confirm(`Revoke portal access for ${drv.name}?`)) return;
    try {
      await api.delete(`/drivers/${drv.driver_id}/portal-access`);
      toast.success(`${drv.name}'s portal access revoked`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not revoke access"));
    }
  };

  const copy = (txt, label) => {
    navigator.clipboard.writeText(txt).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Copy failed — select & copy manually"),
    );
  };

  const load = () => api.get("/drivers").then((r) => setDrivers(r.data)).catch(() => {});
  useEffect(() => {
    load();
    // Pull the company-wide default driver's share % so new / mode-swap drivers
    // match whatever the dispatcher last set on the Pay Run page.
    api.get("/settings").then((r) => {
      const p = Number(r.data?.default_driver_pct);
      const b = Number(r.data?.default_back_office_pct);
      const m = Number(r.data?.pay_run?.default_pay_per_mile);
      if (!Number.isNaN(p) && p > 0) setDefaultDriverPct(p);
      if (!Number.isNaN(b) && b >= 0) setDefaultBoPct(b);
      if (!Number.isNaN(m) && m > 0) setDefaultPayPerMile(m);
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return drivers.filter((d) => {
      if (statusFilter === "active" && !d.active) return false;
      if (statusFilter === "inactive" && d.active) return false;
      if (!q) return true;
      return (
        d.name.toLowerCase().includes(q) ||
        (d.display_id || "").toLowerCase().includes(q) ||
        (d.phone || "").toLowerCase().includes(q)
      );
    });
  }, [drivers, query, statusFilter]);

  const openNew = () => { setForm(buildEmpty(defaultDriverPct, defaultBoPct, defaultPayPerMile)); setEditing(null); setOpen(true); };
  const openEdit = (d) => { setForm({ ...buildEmpty(defaultDriverPct, defaultBoPct, defaultPayPerMile), ...d }); setEditing(d.driver_id); setOpen(true); };

  const save = async () => {
    try {
      const payload = {
        name: (form.name || "").trim(),
        pay_type: form.pay_type,
        pay_rate: parseFloat(form.pay_rate) || 0,
        role: form.role,
        active: form.active !== false,
        base_rate_per_mile: form.base_rate_per_mile === "" ? 0 : (parseFloat(form.base_rate_per_mile) || 0),
        default_back_office_percent: form.default_back_office_percent === "" ? 0 : (parseFloat(form.default_back_office_percent) || 0),
        custom_values: form.custom_values || {},
        cards: (form.cards || [])
          .map((c) => ({
            card_id: c.card_id || undefined,
            label: (c.label || "").trim(),
            last4: (c.last4 || "").replace(/\D/g, "").slice(-4),
          }))
          .filter((c) => c.label || c.last4),
        weekly_deductions: (form.weekly_deductions || [])
          .filter((w) => (w.label || "").trim() !== "")
          .map((w) => ({
            label: w.label.trim(),
            amount: parseFloat(w.amount) || 0,
            kind: w.kind || "deduction",
            note: w.note || "",
          })),
      };
      // Only send optional fields when they have real values -- empty strings
      // trip Pydantic's EmailStr / Optional validators and return a 422.
      const dispId = (form.display_id || "").trim();
      if (dispId) payload.display_id = dispId;
      const email = (form.email || "").trim();
      if (email) payload.email = email;
      const phone = (form.phone || "").trim();
      if (phone) payload.phone = phone;
      const partnerId = (form.team_partner_id || "").trim();
      if (partnerId) payload.team_partner_id = partnerId;

      if (!payload.name) { toast.error("Driver name is required"); return; }
      if (editing) await api.put(`/drivers/${editing}`, payload);
      else await api.post("/drivers", payload);
      toast.success("Driver saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e, "Failed to save driver")); }
  };

  const addWeekly = () =>
    setForm((f) => ({
      ...f,
      weekly_deductions: [...(f.weekly_deductions || []), { _rid: crypto.randomUUID(), label: "", amount: "", kind: "deduction", note: "" }],
    }));
  const updWeekly = (i, k, v) =>
    setForm((f) => {
      const n = [...(f.weekly_deductions || [])];
      n[i] = { ...n[i], [k]: v };
      return { ...f, weekly_deductions: n };
    });
  const rmWeekly = (i) =>
    setForm((f) => ({ ...f, weekly_deductions: (f.weekly_deductions || []).filter((_, idx) => idx !== i) }));

  // Company cards (fuel / credit / Comdata etc.) -- repeatable. Blank slots
  // start empty so admins can fill in last 4 digits now or later; a totally
  // empty row is dropped on save server-side.
  const addCard = () =>
    setForm((f) => ({
      ...f,
      cards: [...(f.cards || []), { _rid: crypto.randomUUID(), label: "", last4: "" }],
    }));
  const updCard = (i, k, v) =>
    setForm((f) => {
      const n = [...(f.cards || [])];
      // Clamp last-4 to digits only, max length 4, as the admin types.
      const value = k === "last4" ? String(v).replace(/\D/g, "").slice(0, 4) : v;
      n[i] = { ...n[i], [k]: value };
      return { ...f, cards: n };
    });
  const rmCard = (i) =>
    setForm((f) => ({ ...f, cards: (f.cards || []).filter((_, idx) => idx !== i) }));

  const remove = async (id) => {
    if (!window.confirm("Remove this driver?")) return;
    await api.delete(`/drivers/${id}`);
    toast.success("Removed");
    load();
  };

  const openStatement = async (d) => {
    setStmtDriver(d);
    setStmtPayDate(new Date().toISOString().slice(0, 10));
    setStmtStart(""); setStmtEnd("");
    const r = await api.get("/trips", { params: { driver_id: d.driver_id, status: "pending" } });
    setPendingTrips(r.data);
    setStmtTripIds([]);
    setStmtOpen(true);
  };

  const toggleTrip = (id) =>
    setStmtTripIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const generateStatement = async () => {
    if (!stmtPayDate || !stmtStart || !stmtEnd || stmtTripIds.length === 0) {
      toast.error("Pay date, period and at least one trip required");
      return;
    }
    try {
      const r = await api.post("/pay-statements", {
        driver_id: stmtDriver.driver_id,
        pay_date: stmtPayDate,
        period_start: stmtStart,
        period_end: stmtEnd,
        trip_ids: stmtTripIds,
        extra_adjustments: [],
      });
      toast.success(`Statement ${r.data.statement_number} created`);
      setStmtOpen(false);
      nav(`/pay-statements/${r.data.statement_id}`);
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  return (
    <div className="space-y-6" data-testid="drivers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Drivers</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Driver records, contact details, status and the default rates that feed trip pay and the pay calculator.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-driver-btn">
              <Plus size={16} className="mr-1.5" /> New driver
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit" : "New"} Driver</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Full Name</Label>
                <Input data-testid="driver-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Driver ID <span className="text-xs text-slate-400">(leave blank to auto-generate)</span></Label>
                <Input data-testid="driver-display-id" placeholder="e.g. 291231 or JDOE" value={form.display_id || ""}
                       onChange={(e) => setForm({ ...form, display_id: e.target.value })} />
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="driver-email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="driver-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <Label>Pay Mode</Label>
                <Select value={form.pay_type} onValueChange={(v) => setForm((f) => {
                  // Auto-swap pay_rate to a sensible default so switching modes
                  // never leaves "0.5" being interpreted as 0.5% (back office 99.5%).
                  const curr = parseFloat(f.pay_rate) || 0;
                  let nextRate = f.pay_rate;
                  if (v === "per_percentage" && curr < 5) nextRate = defaultDriverPct;  // use company-wide default
                  else if (v === "per_mile" && curr > 10) nextRate = defaultPayPerMile || 0.5;     // $/mile default
                  return { ...f, pay_type: v, pay_rate: nextRate };
                })}>
                  <SelectTrigger data-testid="driver-pay-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_mile">Pay Per Mile</SelectItem>
                    <SelectItem value="per_percentage">Pay Per Percentage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{form.pay_type === "per_mile" ? "Rate ($/mile)" : "Driver's share %"}</Label>
                <Input data-testid="driver-pay-rate" type="number" step="0.01" value={form.pay_rate}
                       onChange={(e) => setForm({ ...form, pay_rate: e.target.value })} />
                {form.pay_type === "per_percentage" && (
                  <div className="text-[11px] text-slate-500 mt-1">
                    Driver takes {(parseFloat(form.pay_rate) || 0).toFixed(0)}% · Back office keeps {Math.max(0, 100 - (parseFloat(form.pay_rate) || 0)).toFixed(0)}%
                  </div>
                )}
              </div>
              {form.pay_type === "per_percentage" && (
                <div>
                  <Label>Base rate per mile ($)</Label>
                  <Input data-testid="driver-base-rate" type="number" step="0.01"
                         value={form.base_rate_per_mile}
                         onChange={(e) => setForm({ ...form, base_rate_per_mile: e.target.value })} />
                  <div className="text-[11px] text-slate-500 mt-1">Market rate the LOAD pays per mile (e.g. 3.05). Driver earns their % of this; fuel surcharge is added at 100%.</div>
                </div>
              )}
              {form.pay_type === "per_percentage" && (
                <div>
                  <Label>Default Back Office %</Label>
                  <div className="flex gap-2">
                    <Select value={
                      Number(form.default_back_office_percent) === 35 ? "company"
                      : Number(form.default_back_office_percent) === 28 ? "own"
                      : "custom"
                    } onValueChange={(v) => setForm({ ...form,
                      default_back_office_percent: v === "company" ? 35 : v === "own" ? 28 : form.default_back_office_percent,
                    })}>
                      <SelectTrigger className="w-44" data-testid="driver-bo-preset"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="company">Company trailer · 35%</SelectItem>
                        <SelectItem value="own">Own trailer · 28%</SelectItem>
                        <SelectItem value="custom">Custom %</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="number" step="0.1" data-testid="driver-default-bo"
                           className="flex-1 text-right font-mono"
                           value={form.default_back_office_percent}
                           onChange={(e) => setForm({ ...form, default_back_office_percent: e.target.value })} />
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    Pre-fills every new trip. Back office takes this slice FIRST (expense). Then the driver's % above applies to what's left.
                  </div>
                </div>
              )}
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="driver-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single Driver</SelectItem>
                    <SelectItem value="team">Team Driver</SelectItem>
                    <SelectItem value="owner_operator">Owner Operator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.active ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, active: v === "yes" })}>
                  <SelectTrigger data-testid="driver-active"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Active</SelectItem>
                    <SelectItem value="no">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Weekly recurring deductions / reimbursements -- applied ONCE per statement */}
            <div className="mt-5 border-t border-slate-200 pt-4" data-testid="weekly-deductions-section">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-semibold text-sm text-slate-900">Weekly Deductions / Reimbursements</div>
                  <div className="text-xs text-slate-500">Recurring items applied once per pay statement (e.g. Insurance, ELD fee). NOT multiplied by trip count.</div>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={addWeekly} data-testid="add-weekly-ded-btn">
                  <Plus size={14} className="mr-1" /> Add weekly item
                </Button>
              </div>
              {(form.weekly_deductions || []).length === 0 && (
                <div className="text-xs text-slate-400 italic py-2">No weekly items. Add Insurance, ELD fee, truck lease, cash advance payback, etc.</div>
              )}
              {(form.weekly_deductions || []).map((w, i) => (
                <div key={w._rid || `w-${i}`} className="grid grid-cols-12 gap-2 mb-2" data-testid={`weekly-row-${i}`}>
                  <div className="col-span-4">
                    <Input placeholder="Label (e.g. Insurance)" value={w.label || ""}
                           onChange={(e) => updWeekly(i, "label", e.target.value)}
                           data-testid={`weekly-label-${i}`} />
                  </div>
                  <div className="col-span-3">
                    <Select value={w.kind || "deduction"} onValueChange={(v) => updWeekly(i, "kind", v)}>
                      <SelectTrigger data-testid={`weekly-kind-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="deduction">Deduction</SelectItem>
                        <SelectItem value="reimbursement">Reimbursement</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Input type="number" step="0.01" placeholder="Amount"
                           className="text-right font-mono"
                           value={w.amount}
                           onChange={(e) => updWeekly(i, "amount", e.target.value)}
                           data-testid={`weekly-amount-${i}`} />
                  </div>
                  <div className="col-span-2 flex items-center">
                    <Button type="button" size="icon" variant="ghost" onClick={() => rmWeekly(i)} data-testid={`weekly-remove-${i}`}>
                      <Trash2 size={14} className="text-rose-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <CustomFieldsInputs
              scope="driver"
              values={form.custom_values}
              onChange={(v) => setForm({ ...form, custom_values: v })}
            />
            {/* Company cards (fuel / credit / Comdata). Driver picks one in the
                portal when a receipt was paid by the company so it does NOT
                affect their paycheck. Admin can add/remove at any time. */}
            <div className="mt-5 border-t border-slate-200 pt-4" data-testid="company-cards-section">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-semibold text-sm text-slate-900">Company Cards <span className="text-xs text-slate-500 font-normal">(optional)</span></div>
                  <div className="text-xs text-slate-500">
                    Last 4 digits of each fuel / credit card issued to this driver. When they submit a receipt in the portal they can tag which card paid — those receipts are logged as a <strong>company expense</strong> and never deduct from or reimburse against their pay. Leave blank if they only use personal cards.
                  </div>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={addCard} data-testid="add-card-btn">
                  <Plus size={14} className="mr-1" /> Add card
                </Button>
              </div>
              {(form.cards || []).length === 0 && (
                <div className="text-xs text-slate-400 italic py-2">
                  No company cards yet. Click <strong>Add card</strong> if this driver carries a fleet fuel card, Comdata, company Visa, etc.
                </div>
              )}
              {(form.cards || []).map((c, i) => (
                <div key={c._rid || c.card_id || `c-${i}`} className="grid grid-cols-12 gap-2 mb-2" data-testid={`card-row-${i}`}>
                  <div className="col-span-6">
                    <Input
                      placeholder="Label (e.g. Comdata, Fleet Visa)"
                      value={c.label || ""}
                      onChange={(e) => updCard(i, "label", e.target.value)}
                      data-testid={`card-label-${i}`}
                    />
                  </div>
                  <div className="col-span-4">
                    <Input
                      placeholder="Last 4 digits"
                      inputMode="numeric"
                      maxLength={4}
                      className="text-right font-mono tracking-widest"
                      value={c.last4 || ""}
                      onChange={(e) => updCard(i, "last4", e.target.value)}
                      data-testid={`card-last4-${i}`}
                    />
                  </div>
                  <div className="col-span-2 flex items-center">
                    <Button type="button" size="icon" variant="ghost" onClick={() => rmCard(i)} data-testid={`card-remove-${i}`}>
                      <Trash2 size={14} className="text-rose-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save} data-testid="save-driver-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Table card */}
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="eyebrow">{filtered.length} drivers</div>
            <div className="text-xs text-slate-500">Select a row for the full driver profile, trips and pay history</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Search name, ID, phone..." value={query} onChange={(e) => setQuery(e.target.value)}
                     className="pl-8 h-9 w-56" data-testid="drivers-search" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-36" data-testid="drivers-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-4 py-2.5">Driver ID</th>
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Phone</th>
                <th className="px-4 py-2.5">Pay Mode</th>
                <th className="px-4 py-2.5 text-right">Rate</th>
                <th className="px-4 py-2.5">Role</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center text-slate-500 py-10">No drivers.</td></tr>
              )}
              {filtered.map((d) => (
                <tr key={d.driver_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`driver-row-${d.display_id}`}>
                  <td className="px-4 py-2.5 font-mono text-xs">{d.display_id}</td>
                  <td className="px-4 py-2.5 font-medium text-slate-900">{d.name}</td>
                  <td className="px-4 py-2.5">
                    <span className={d.active ? "pill-green" : "pill-gray"}>{d.active ? "Active" : "Inactive"}</span>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{d.phone || "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className={d.pay_type === "per_mile" ? "pill-blue" : "pill-amber"}>
                      {d.pay_type === "per_mile" ? "Per Mile" : "Per %"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono">
                    {d.pay_type === "per_mile" ? `$${Number(d.pay_rate).toFixed(2)}/mi` : `${Number(d.pay_rate).toFixed(1)}%`}
                  </td>
                  <td className="px-4 py-2.5 capitalize text-slate-600">{d.role.replace("_", " ")}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="mr-1" onClick={() => openStatement(d)} data-testid={`driver-statement-${d.display_id}`}>
                      <FileText size={14} className="mr-1" /> Statement
                    </Button>
                    {d.portal_pin ? (
                      <Button size="sm" variant="outline" className="mr-1 border-emerald-400 text-emerald-700 hover:bg-emerald-50" title="Portal access ON — click to revoke" onClick={() => disablePortal(d)} data-testid={`portal-off-${d.display_id}`}>
                        <Smartphone size={14} className="mr-1" /> Portal ON
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="mr-1 border-blue-400 text-blue-700 hover:bg-blue-50" title="Generate a 6-digit PIN so this driver can log in to the driver portal" onClick={() => enablePortal(d)} data-testid={`portal-on-${d.display_id}`}>
                        <Smartphone size={14} className="mr-1" /> Give PIN
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => openEdit(d)} data-testid={`edit-driver-${d.display_id}`}>
                      <Pencil size={14} />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(d.driver_id)} data-testid={`delete-driver-${d.display_id}`}>
                      <Trash2 size={14} className="text-rose-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Generate statement dialog */}
      <Dialog open={stmtOpen} onOpenChange={setStmtOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generate pay statement — {stmtDriver?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              Statement # auto-assigned as <strong>MMDDYYYY-XX</strong> from the pay date (XX increments 00, 01, 02…
              per driver on that pay date).
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Pay Date</Label>
                <Input type="date" value={stmtPayDate} onChange={(e) => setStmtPayDate(e.target.value)} data-testid="stmt-pay-date" />
              </div>
              <div>
                <Label>Period Start</Label>
                <Input type="date" value={stmtStart} onChange={(e) => setStmtStart(e.target.value)} data-testid="stmt-period-start" />
              </div>
              <div>
                <Label>Period End</Label>
                <Input type="date" value={stmtEnd} onChange={(e) => setStmtEnd(e.target.value)} data-testid="stmt-period-end" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Pick trips</Label>
                {pendingTrips.length > 0 && (
                  <div className="flex gap-2 text-xs">
                    <button onClick={() => setStmtTripIds(pendingTrips.map((t) => t.trip_id))} className="text-blue-700 hover:underline">Select all</button>
                    <span className="text-slate-300">·</span>
                    <button onClick={() => setStmtTripIds([])} className="text-slate-500 hover:underline">Clear</button>
                  </div>
                )}
              </div>
              <div className="border rounded max-h-56 overflow-y-auto divide-y">
                {pendingTrips.length === 0 && <p className="text-xs text-slate-500 p-3">No pending trips. Log one in Trips.</p>}
                {pendingTrips.map((t) => (
                  <label key={t.trip_id} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-slate-50">
                    <input type="checkbox" checked={stmtTripIds.includes(t.trip_id)} onChange={() => toggleTrip(t.trip_id)} />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs font-semibold">{t.trip_number} <span className="text-slate-400 font-sans">· {t.load_date}</span></div>
                      <div className="text-xs text-slate-500 truncate">
                        {t.origin_city}, {t.origin_state} → {t.destination_city}, {t.destination_state} · {t.paid_miles} mi {t.is_team && " · Team"}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStmtOpen(false)}>Cancel</Button>
            <Button onClick={generateStatement} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="generate-statement-btn">
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Driver Portal credentials dialog */}
      <Dialog open={!!portalInfo} onOpenChange={(o) => !o && setPortalInfo(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="portal-credentials-dialog">
          <DialogHeader>
            <DialogTitle>Portal access for {portalInfo?.driver?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {portalInfo?.email_sent ? (
              <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="portal-email-ok">
                <div className="font-semibold mb-1">Email sent to {portalInfo.email_to}</div>
                The driver received the portal link, PIN and instructions. Still copy the PIN below if you also want to text it. Clicking "Give PIN" again generates a brand-new PIN and sends another email.
              </div>
            ) : portalInfo?.driver?.email ? (
              <div className="rounded border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900 min-w-0">
                <div className="font-semibold mb-1">Email failed to send</div>
                <div className="mb-2 break-all">Tried: <span className="font-mono">{portalInfo.driver.email}</span></div>
                {portalInfo.email_error && (
                  <div className="mb-2 text-[11px] bg-white rounded px-2 py-1 border border-rose-200 font-mono break-all whitespace-pre-wrap max-h-28 overflow-y-auto">
                    {portalInfo.email_error.length > 400 ? portalInfo.email_error.slice(0, 400) + "…" : portalInfo.email_error}
                  </div>
                )}
                <div className="text-[11px]">
                  Common fixes: typo in the driver's email, test-only domain (like <span className="font-mono">example.com</span>), or the driver's inbox rejected us. Fix the address in the driver profile and click "Give PIN" again, or copy the credentials below and send them manually (SMS / WhatsApp).
                </div>
              </div>
            ) : (
              <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <div className="font-semibold mb-1">No email on file for this driver</div>
                Add an email to the driver profile so you can send the PIN automatically next time. For now, copy the credentials below and send them manually.
              </div>
            )}
            <div>
              <Label className="text-xs">Portal link</Label>
              <div className="flex gap-2 mt-1">
                <Input value={portalInfo?.url || ""} readOnly className="font-mono text-xs" />
                <Button size="icon" variant="outline" onClick={() => copy(portalInfo.url, "Link")} data-testid="copy-portal-url">
                  <Copy size={14} />
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">6-digit PIN</Label>
              <div className="flex gap-2 mt-1">
                <Input value={portalInfo?.pin || ""} readOnly className="font-mono text-lg tracking-widest text-center" />
                <Button size="icon" variant="outline" onClick={() => copy(portalInfo.pin, "PIN")} data-testid="copy-portal-pin">
                  <Copy size={14} />
                </Button>
              </div>
            </div>
            <div className="text-[11px] text-slate-500">
              Driver taps the link → types the PIN → sees their own pay stubs. They never see other drivers' data.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPortalInfo(null)}>Done</Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={() => {
                if (window.confirm(`Revoke portal access for ${portalInfo.driver.name}?`)) {
                  api.delete(`/drivers/${portalInfo.driver.driver_id}/portal-access`).then(() => {
                    toast.success("Portal access revoked");
                    setPortalInfo(null);
                    load();
                  });
                }
              }}
              data-testid="revoke-portal-btn"
            >
              Revoke access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
